export const SLIDE_IMAGE_MAX_UPLOAD_BYTES = 5 * 1024 * 1024;
export const SLIDE_IMAGE_MAX_EDGE_PX = 1920;
export const SLIDE_IMAGE_WEBP_QUALITY = 0.8;

export interface CompressedSlideImage {
  blob: Blob;
  width: number;
  height: number;
  mimeType: string;
}

function loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('이미지를 읽을 수 없습니다.'));
    };
    img.src = url;
  });
}

function scaleDimensions(
  width: number,
  height: number,
  maxEdge: number
): { width: number; height: number } {
  const long = Math.max(width, height);
  if (long <= maxEdge) return { width, height };
  const scale = maxEdge / long;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, mime: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (!b) reject(new Error('이미지 압축에 실패했습니다.'));
        else resolve(b);
      },
      mime,
      quality
    );
  });
}

/** 클립보드·파일 이미지를 WebP(미지원 시 JPEG)로 압축. 최대 5MB. */
export async function compressClipboardImage(file: Blob): Promise<CompressedSlideImage> {
  const img = await loadImageFromBlob(file);
  const { width, height } = scaleDimensions(img.naturalWidth, img.naturalHeight, SLIDE_IMAGE_MAX_EDGE_PX);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('이미지 압축을 시작할 수 없습니다.');
  ctx.drawImage(img, 0, 0, width, height);

  let mime = 'image/webp';
  let blob = await canvasToBlob(canvas, mime, SLIDE_IMAGE_WEBP_QUALITY).catch(() => null);
  if (!blob) {
    mime = 'image/jpeg';
    blob = await canvasToBlob(canvas, mime, SLIDE_IMAGE_WEBP_QUALITY);
  }

  if (blob.size > SLIDE_IMAGE_MAX_UPLOAD_BYTES) {
    throw new Error('압축 후에도 5MB를 초과합니다. 더 작은 이미지를 붙여넣어 주세요.');
  }

  return { blob, width, height, mimeType: mime };
}

/** 버튼 클릭 등 사용자 제스처 직후 Clipboard API로 이미지 읽기 */
export async function readClipboardImageBlobFromApi(): Promise<Blob | null> {
  if (typeof navigator === 'undefined' || !navigator.clipboard?.read) {
    return null;
  }
  try {
    const items = await navigator.clipboard.read();
    for (const item of items) {
      for (const type of item.types) {
        if (!type.startsWith('image/')) continue;
        return await item.getType(type);
      }
    }
  } catch {
    return null;
  }
  return null;
}

export function readClipboardImageBlob(clipboardData: DataTransfer | null): Blob | null {
  if (!clipboardData) return null;
  const items = clipboardData.items;
  if (items) {
    for (let i = 0; i < items.length; i += 1) {
      const item = items[i];
      if (item.kind === 'file' && item.type.startsWith('image/')) {
        const f = item.getAsFile();
        if (f) return f;
      }
    }
  }
  const files = clipboardData.files;
  if (files?.length) {
    for (let i = 0; i < files.length; i += 1) {
      const f = files[i];
      if (f.type.startsWith('image/')) return f;
    }
  }
  return null;
}
